import { useState } from "react";
import { nextAvailableControlRuleName, type ControlRule } from "../model/inp/controlRules.ts";
import {
  canEditVisually,
  serializeControlRule,
  type RuleVariable,
} from "../engine/controlRules/controlRuleParser.ts";
import { useT } from "../i18n/hooks.ts";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";
import { ControlRuleBuilder, type ControlRuleDraft } from "./ControlRuleBuilder.tsx";
import { defaultControlRuleDraft, type ElementNames } from "./controlRuleDraft.ts";

// A rule the structured builder can't safely represent (unparseable body,
// or a real VARIABLE/EXPRESSION reference) falls back to the plain text
// box automatically; a manual toggle also lets the user force text mode
// for any rule, so nothing is ever out of reach.
export function ControlRulesEditor({
  rules,
  onChange,
  selectedName,
  onSelect,
  namedVariables,
  expressionNames,
  names,
  curveNames,
  timeSeriesNames,
}: {
  rules: ControlRule[];
  onChange: (rules: ControlRule[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
  namedVariables: ReadonlyMap<string, RuleVariable>;
  expressionNames: ReadonlySet<string>;
  names: ElementNames;
  curveNames: string[];
  timeSeriesNames: string[];
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteRule,
    rename: renameSelected,
    update,
  } = useNamedCollection(rules, onChange, selectedName, onSelect);

  function addRule() {
    const name = nextAvailableControlRuleName(new Set(rules.map((r) => r.name)));
    add({ name, body: "" });
  }

  function setBody(body: string) {
    update((r) => ({ ...r, body }));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={rules}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addRule}
        addLabel={t("controlRules.addRule")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("controlRules.noRuleSelected")}</p>}
        {selected && (
          <RuleDetail
            key={selected.name}
            rule={selected}
            onChangeBody={setBody}
            onRename={renameSelected}
            onDelete={() => deleteRule(selected.name)}
            namedVariables={namedVariables}
            expressionNames={expressionNames}
            names={names}
            curveNames={curveNames}
            timeSeriesNames={timeSeriesNames}
          />
        )}
      </div>
    </div>
  );
}

// Keyed by rule.name in the parent above, so switching rules always gets
// a fresh mount instead of carrying over mode/draft/visualError state.
function RuleDetail({
  rule,
  onChangeBody,
  onRename,
  onDelete,
  namedVariables,
  expressionNames,
  names,
  curveNames,
  timeSeriesNames,
}: {
  rule: ControlRule;
  onChangeBody: (body: string) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  namedVariables: ReadonlyMap<string, RuleVariable>;
  expressionNames: ReadonlySet<string>;
  names: ElementNames;
  curveNames: string[];
  timeSeriesNames: string[];
}) {
  const t = useT();
  const isEmptyBody = rule.body.trim() === "";

  const [mode, setMode] = useState<"visual" | "text">(() => {
    if (isEmptyBody) return "visual";
    return canEditVisually(rule, namedVariables, expressionNames) ? "visual" : "text";
  });
  const [draft, setDraft] = useState<ControlRuleDraft>(() => {
    if (isEmptyBody) return defaultControlRuleDraft(names);
    return canEditVisually(rule, namedVariables, expressionNames) ?? defaultControlRuleDraft(names);
  });
  const [visualError, setVisualError] = useState<string | null>(null);

  function updateDraft(next: ControlRuleDraft) {
    setDraft(next);
    onChangeBody(serializeControlRule(next));
  }

  function switchToVisual() {
    const parsed =
      rule.body.trim() === "" ? null : canEditVisually(rule, namedVariables, expressionNames);
    if (!parsed && rule.body.trim() !== "") {
      setVisualError(t("controlRules.cannotEditVisually"));
      return;
    }
    setVisualError(null);
    setDraft(parsed ?? defaultControlRuleDraft(names));
    setMode("visual");
  }

  return (
    <>
      <NamedCollectionHeader
        name={rule.name}
        onRename={onRename}
        onDelete={onDelete}
        deleteLabel={t("controlRules.deleteRule")}
      />
      <div className="control-rule-mode-toggle" role="tablist">
        <button
          className={mode === "visual" ? "button tab-active" : "button"}
          role="tab"
          aria-selected={mode === "visual"}
          onClick={switchToVisual}
        >
          {t("controlRules.visualMode")}
        </button>
        <button
          className={mode === "text" ? "button tab-active" : "button"}
          role="tab"
          aria-selected={mode === "text"}
          onClick={() => setMode("text")}
        >
          {t("controlRules.textMode")}
        </button>
      </div>
      {mode === "visual" ? (
        <ControlRuleBuilder
          draft={draft}
          onChange={updateDraft}
          names={names}
          curveNames={curveNames}
          timeSeriesNames={timeSeriesNames}
        />
      ) : (
        <>
          {visualError && (
            <p className="property-panel-hint control-rule-visual-error">{visualError}</p>
          )}
          <p className="property-panel-hint">{t("controlRules.bodyHint")}</p>
          <textarea
            className="control-rule-body"
            value={rule.body}
            onChange={(e) => onChangeBody(e.target.value)}
            placeholder={"IF NODE N1 DEPTH >= 4\nTHEN PUMP P1 status = ON\nPRIORITY 1"}
            spellCheck={false}
          />
        </>
      )}
    </>
  );
}
